import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { Input } from "@/workflow_api/types/workflow";
import type { Variable } from "../../types";
import type { NodeVariable } from "../../types";
import {
  getAttachmentsFromInput,
  getCommentFromInput,
  getStatusFromInput,
  serializeResolutionInputs,
} from "../serialization";

interface ResolutionContextType {
  status: string;
  setStatus: (status: string) => void;
  commentVariable: Variable | null;
  setCommentVariable: (variable: Variable | null) => void;
  commentValue: string;
  setCommentValue: (value: string) => void;
  attachments: string[];
  setAttachments: (attachments: string[]) => void;
  commentIsVariable: boolean;
  setCommentIsVariable: (isVariable: boolean) => void;
  isValid: boolean;
  statusValid: boolean;
  getInputs: () => Input;
  reset: () => void;
}

const ResolutionContext = createContext<ResolutionContextType>({
  status: "",
  setStatus: () => {},
  commentVariable: null,
  setCommentVariable: () => {},
  commentValue: "",
  setCommentValue: () => {},
  commentIsVariable: false,
  setCommentIsVariable: () => {},
  attachments: [],
  setAttachments: () => {},
  isValid: false,
  statusValid: false,
  getInputs: () => [],
  reset: () => {},
});

export const ResolutionProvider = ({
  children,
  inputs,
  variables,
}: {
  children: React.ReactNode;
  inputs: Input;
  variables?: NodeVariable[];
}) => {
  const [status, setStatus] = useState<string>("");
  const [commentIsVariable, setCommentIsVariable] = useState<boolean>(false);
  const [commentVariable, setCommentVariable] = useState<Variable | null>(null);
  const [commentValue, setCommentValue] = useState<string>("");
  const [attachments, setAttachments] = useState<string[]>([]);

  useEffect(() => {
    if (inputs && inputs.length > 0 && variables) {
      const status = getStatusFromInput(inputs);
      setStatus(status);
      const { variable, value, isVariable } = getCommentFromInput(
        inputs,
        variables,
      );
      setCommentVariable(variable);
      setCommentValue(value);
      setCommentIsVariable(isVariable);
      if (status) {
        setAttachments(getAttachmentsFromInput(inputs, variables));
      }
    }
  }, [inputs, variables]);

  const isValid = useMemo(() => status !== "", [status]);
  const statusValid = useMemo(() => status !== "", [status]);

  const getInputs: () => Input = () => {
    try {
      return serializeResolutionInputs(
        status,
        attachments,
        commentValue,
        commentVariable,
        variables,
      );
    } catch (error) {
      console.error("Error generating inputs:", error);
    }
    return [];
  };

  const reset = () => {
    setStatus("");
    setCommentVariable(null);
    setCommentValue("");
    setAttachments([]);
  };

  return (
    <ResolutionContext.Provider
      value={{
        status,
        setStatus,
        commentVariable,
        setCommentVariable,
        commentValue,
        setCommentValue,
        commentIsVariable,
        setCommentIsVariable,
        attachments,
        setAttachments,
        isValid,
        statusValid,
        getInputs,
        reset,
      }}
    >
      {children}
    </ResolutionContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useResolution = () => {
  const context = useContext(ResolutionContext);
  if (context === undefined) {
    throw new Error("useResolution must be used within a ResolutionProvider");
  }
  return context;
};
